import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_TESTIMONIES = [
  {
    persona: 'Owner',
    name: 'Budi Santoso',
    role: 'Owner, Kost Melati Kemang',
    content: 'Sejak bekerjasama dengan ISK, saya tidak perlu lagi pusing urusan operasional kost. Occupancy rate naik drastis, maintenance teratur, dan income bulanan menjadi jauh lebih stabil.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1549923746-c502d488b3ea?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    persona: 'Partner',
    name: 'Dewi Lestari',
    role: 'Partner, Urban Living Senopati',
    content: 'ISK memberikan partner operasional yang bisa dipercaya. Dari tahap pengembangan sampai operasional, timnya rapi, komunikatif, dan membuat investasi terasa lebih terkendali.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1698047681452-08eba22d0c64?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    persona: 'Tenant',
    name: 'Rina Maharani',
    role: 'Tenant, Kost Mawar Tebet',
    content: 'Sebagai tenant, saya merasa tinggal di properti yang lebih nyaman dan tertata. Respon terhadap kendala cepat, area bersama bersih, dan komunikasi dengan tim pengelola sangat membantu.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=85'
  }
];

const PERSONA_ORDER = ['Owner', 'Partner', 'Tenant'];

const detectPersona = (testimonial) => {
  const role = `${testimonial.role || ''}`.toLowerCase();
  if (role.includes('tenant')) return 'Tenant';
  if (role.includes('partner') || role.includes('investor')) return 'Partner';
  return 'Owner';
};

const normalizeTestimonials = (items) => {
  const source = Array.isArray(items) && items.length > 0 ? items : FALLBACK_TESTIMONIES;
  const byPersona = new Map();

  source.forEach((item) => {
    const persona = detectPersona(item);
    if (!byPersona.has(persona)) {
      byPersona.set(persona, {
        ...item,
        persona,
      });
    }
  });

  return PERSONA_ORDER.map((persona) => {
    const existing = byPersona.get(persona);
    if (existing) return existing;
    return FALLBACK_TESTIMONIES.find((item) => item.persona === persona);
  });
};

const personaTone = {
  Owner: 'bg-isk-teal/10 text-isk-teal',
  Partner: 'bg-isk-gold/15 text-isk-gold-hover',
  Tenant: 'bg-isk-teal/10 text-isk-teal'
};

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    containScroll: 'trimSnaps',
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
        setTestimonials(normalizeTestimonials(response.data));
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials(FALLBACK_TESTIMONIES);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    emblaApi.reInit();
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, testimonials.length]);

  if (loading) {
    return null;
  }

  const renderCard = (testimonial, index, wrapperClassName = '', testIdPrefix = 'testimonial') => (
    <div
      key={`${testimonial.persona}-${testimonial.name}-${index}`}
      className={wrapperClassName}
      data-testid={`${testIdPrefix}-${index}`}
    >
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.08 }}
        className="glass-card rounded-2xl p-8 h-full flex flex-col"
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={testimonial.image}
              alt={testimonial.name}
              className="w-14 h-14 rounded-2xl object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold mb-2 ${personaTone[testimonial.persona]}`}>
                {testimonial.persona}
              </div>
              <h3 className="text-lg font-bold text-isk-teal leading-tight truncate">{testimonial.name}</h3>
              <p className="text-sm text-isk-teal/60 leading-relaxed">{testimonial.role}</p>
            </div>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-isk-teal/10 text-isk-teal flex items-center justify-center flex-shrink-0">
            <Quote className="w-5 h-5" />
          </div>
        </div>

        <div className="flex items-center gap-1 mb-5 text-isk-gold">
          {Array.from({ length: testimonial.rating }).map((_, starIndex) => (
            <Star key={`${testimonial.persona}-star-${starIndex}`} className="w-4 h-4 fill-current" />
          ))}
        </div>

        <p className="text-sm md:text-base leading-relaxed text-isk-teal/80 flex-1">
          "{testimonial.content}"
        </p>
      </motion.article>
    </div>
  );

  return (
    <section id="testimonials" className="py-20 md:py-32 px-6 lg:px-12 bg-isk-bg" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-isk-teal tracking-tight mb-4">
            Testimonies
          </h2>
          <p className="text-lg text-isk-teal/70 max-w-2xl mx-auto">
            Pendapat dari owner, partner, dan tenant yang sudah merasakan langsung cara kerja ISK.
          </p>
        </motion.div>

        <div className="relative md:hidden">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-5">
              {testimonials.map((testimonial, index) =>
                renderCard(testimonial, index, 'min-w-0 self-stretch flex-[0_0_100%]', 'testimonial-mobile')
              )}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonials.map((testimonial, index) => (
              <button
                key={`${testimonial.persona}-dot`}
                type="button"
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                className={`h-2.5 rounded-full transition-all ${
                  selectedIndex === index ? 'w-8 bg-isk-teal' : 'w-2.5 bg-isk-teal/25'
                }`}
                aria-label={`Lihat testimony ${testimonial.persona}`}
                data-testid={`testimonial-dot-${index}`}
              />
            ))}
          </div>
        </div>

        <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
          {testimonials.map((testimonial, index) => renderCard(testimonial, index, 'h-full', 'testimonial'))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
